import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const hours = [
  { day: 'Monday', time: 'Closed' },
  { day: 'Tuesday', time: '9:00 AM - 5:30 PM' },
  { day: 'Wednesday', time: '9:00 AM - 5:30 PM' },
  { day: 'Thursday', time: '9:00 AM - 8:00 PM' },
  { day: 'Friday', time: '9:00 AM - 5:30 PM' },
  { day: 'Saturday', time: '8:00 AM - 5:00 PM' },
  { day: 'Sunday', time: 'Closed' },
];

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'Message sent successfully! We\'ll be in touch soon.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          message: '',
        });
      } else {
        toast.error(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Unable to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="contact-page">
      <SEO page="contact" />
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24" data-testid="contact-hero">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Get In Touch</p>
            <h1 className="text-5xl md:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed">
              We'd love to hear from you. Reach out with any questions or to book your appointment.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-[#2C2420] mb-8">
                Visit Us
              </h2>

              {/* Location Card */}
              <div className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50 mb-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#8D7B68]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#2C2420] mb-1">Location</h3>
                    <p className="text-[#5D4A3A]">
                      925 Burke Road<br />
                      Camberwell, VIC 3124
                    </p>
                    <a 
                      href="https://maps.google.com/?q=925+Burke+Rd+Camberwell+VIC+3124"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#8D7B68] text-sm font-medium mt-2 inline-block hover:underline"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#8D7B68]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#2C2420] mb-1">Phone</h3>
                    <a 
                      href="tel:0398826969"
                      className="text-[#5D4A3A] hover:text-[#8D7B68] transition-colors"
                    >
                      (03) 9882 6969
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#8D7B68]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-[#2C2420] mb-1">Email</h3>
                    <a 
                      href="mailto:hello@artistryhousehbw.com"
                      className="text-[#5D4A3A] hover:text-[#8D7B68] transition-colors"
                    >
                      hello@artistryhousehbw.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Opening Hours */}
              <div className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-[#D4C4B0]/30 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#8D7B68]" />
                  </div>
                  <h3 className="text-lg font-medium text-[#2C2420]">Opening Hours</h3>
                </div>
                <ul className="space-y-3">
                  {hours.map((item) => (
                    <li key={item.day} className="flex justify-between text-sm">
                      <span className="text-[#5D4A3A]">{item.day}</span>
                      <span className={item.time === 'Closed' ? 'text-[#6B5D4D]' : 'text-[#2C2420] font-medium'}>
                        {item.time}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Book Online CTA */}
              <div className="mt-8 p-6 bg-[#D4C4B0]/20 rounded-lg text-center">
                <p className="text-[#3D352D] mb-4">Prefer to book online?</p>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-6">
                    <Calendar className="w-4 h-4 mr-2" />
                    Book Online
                  </Button>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-medium text-[#2C2420] mb-8">
                Send Us a Message
              </h2>

              <form 
                onSubmit={handleSubmit} 
                className="bg-white p-8 rounded-lg border border-[#D4C4B0]/50"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-[#3D352D] mb-2">
                      First Name *
                    </label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="border-[#D4C4B0] focus:border-[#8D7B68] focus:ring-[#8D7B68]"
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-[#3D352D] mb-2">
                      Last Name *
                    </label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="border-[#D4C4B0] focus:border-[#8D7B68] focus:ring-[#8D7B68]"
                      data-testid="input-last-name"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-[#3D352D] mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-[#D4C4B0] focus:border-[#8D7B68] focus:ring-[#8D7B68]"
                    data-testid="input-email"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="phone" className="block text-sm font-medium text-[#3D352D] mb-2">
                    Phone (Optional)
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border-[#D4C4B0] focus:border-[#8D7B68] focus:ring-[#8D7B68]"
                    data-testid="input-phone"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-[#3D352D] mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="border-[#D4C4B0] focus:border-[#8D7B68] focus:ring-[#8D7B68] resize-none"
                    placeholder="How can we help you?"
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full py-4"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-24 bg-[#D4C4B0]/20" data-testid="map-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-medium text-[#2C2420] mb-4">Find Us</h2>
            <p className="text-[#5D4A3A]">Located in the service precinct at the northern end of Burke Road, Camberwell</p>
          </div>
          
          <div className="aspect-[16/9] md:aspect-[21/9] rounded-lg overflow-hidden map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3150.8661855840047!2d145.05489671531895!3d-37.82561817974897!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad6410d8e8e6a8f%3A0x5045675218ce6e0!2s925%20Burke%20Rd%2C%20Camberwell%20VIC%203124%2C%20Australia!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Artistry House Location"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-medium text-white mb-6">
            Ready to Book?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Skip the contact form and book your appointment online. Choose your service, stylist, and preferred time.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Your Appointment
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
